/**
 * Events page functionality
 * Handles fetching events from the API and pagination
 */

class RenobackEventsController {
    constructor() {
        this.apiUrl = 'http://localhost:3000/api/v1/events';
        this.eventsContainer = document.querySelector('.events-feed');
        this.paginationContainer = document.querySelector('.pagination');
        this.currentPage = 1;
        this.totalPages = 1;
        this.loading = false;
        this.perPage = 12; // Default per page value
        
        this.init();
    }

    init() {
        // Initial events load
        this.fetchEvents();
        
        // Setup pagination click handlers
        if (this.paginationContainer) {
            this.paginationContainer.addEventListener('click', (e) => {
                const target = e.target.closest('.pagination-link');
                if (!target) return;
                
                e.preventDefault();
                
                if (target.classList.contains('newer-posts')) {
                    this.currentPage = Math.max(1, this.currentPage - 1);
                } else if (target.classList.contains('older-posts')) {
                    this.currentPage = Math.min(this.totalPages, this.currentPage + 1);
                }
                
                this.fetchEvents();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    }

    async fetchEvents() {
        if (this.loading) return;
        
        this.loading = true;
        this.showLoading();
        
        try {
            const url = `${this.apiUrl}?page=${this.currentPage}&per_page=${this.perPage}`;
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Update pagination info
            this.currentPage = data.pagination.current_page;
            this.totalPages = data.pagination.total_pages;
            this.perPage = data.pagination.per_page;
            
            // Render events
            this.renderEvents(data.events);
            
            // Update pagination UI
            this.updatePagination();
        } catch (error) {
            console.error('Error fetching events:', error);
            this.renderError('Failed to load events. Please try again later.');
        } finally {
            this.loading = false;
            this.hideLoading();
        }
    }

    renderEvents(events) {
        if (!this.eventsContainer) return;
        
        // Clear existing events
        this.eventsContainer.innerHTML = '';
        
        if (events.length === 0) {
            this.renderEmptyState();
            return;
        }
        
        events.forEach(event => {
            const eventHtml = this.createEventItem(event);
            this.eventsContainer.insertAdjacentHTML('beforeend', eventHtml);
        });
    }

    createEventItem(event) {
        // Safely parse dates with validation
        let startDate, stopDate, deadlineDate;
        
        try {
            startDate = event.start_datetime ? new Date(event.start_datetime) : null;
            // Check if date is valid
            if (startDate && isNaN(startDate.getTime())) {
                startDate = null;
            }
        } catch (e) {
            console.error('Error parsing start date:', e);
            startDate = null;
        }
        
        try {
            stopDate = event.stop_datetime ? new Date(event.stop_datetime) : null;
            // Check if date is valid
            if (stopDate && isNaN(stopDate.getTime())) {
                stopDate = null;
            }
        } catch (e) {
            console.error('Error parsing stop date:', e);
            stopDate = null;
        }
        
        try {
            deadlineDate = event.signup_deadline ? new Date(event.signup_deadline) : null;
            // Check if date is valid
            if (deadlineDate && isNaN(deadlineDate.getTime())) {
                deadlineDate = null;
            }
        } catch (e) {
            console.error('Error parsing deadline date:', e);
            deadlineDate = null;
        }
        
        const formatDate = (date) => {
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        };
        
        const formatTime = (date) => {
            return date.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
            });
        };
        
        // Truncate description to ~200 characters
        const truncateDescription = (text) => {
            if (!text) return '';
            if (text.length <= 200) return text;
            return text.substring(0, 200).trim() + '...';
        };
        
        // Generate image placeholder if no image is provided
        const getImageHtml = () => {
            if (event.image) {
                return `
                    <img 
                        src="${event.image}"
                        alt="${event.name || 'Event'}"
                        loading="lazy"
                    >
                `;
            } else {
                // Return a placeholder with event initial or icon
                const initial = event.name ? event.name.charAt(0).toUpperCase() : 'E';
                return `
                    <div class="event-image-placeholder">
                        <span>${initial}</span>
                    </div>
                `;
            }
        };
        
        return `
            <article class="event-list-item">
                <div class="event-list-image">
                    ${getImageHtml()}
                </div>
                <div class="event-list-content">
                    <h3 class="event-list-title">${event.name || 'Unnamed Event'}</h3>
                    ${startDate ? `
                    <div class="event-dates">
                        <time datetime="${startDate.toISOString()}">
                            ${formatDate(startDate)} at ${formatTime(startDate)}
                        </time>
                        ${stopDate ? `
                            <span class="event-date-separator">to</span>
                            <time datetime="${stopDate.toISOString()}">
                                ${formatDate(stopDate)} at ${formatTime(stopDate)}
                            </time>
                        ` : ''}
                    </div>
                    ` : ''}
                    ${event.description ? `
                        <p class="event-list-excerpt">${truncateDescription(event.description)}</p>
                    ` : ''}
                    <div class="event-list-footer">
                        <a href="${event.signup_link}" class="primary-btn sans-serif">
                            ${event.call_to_action || 'Learn More'}
                        </a>
                    </div>
                </div>
            </article>
        `;
    }

    renderEmptyState() {
        this.eventsContainer.innerHTML = `
            <div class="events-empty-state">
                <h3>No events found</h3>
                <p>There are currently no upcoming events scheduled.</p>
            </div>
        `;
    }

    renderError(message) {
        this.eventsContainer.innerHTML = `
            <div class="events-error">
                <h3>Error</h3>
                <p>${message}</p>
                <button class="primary-btn sans-serif retry-btn">Try Again</button>
            </div>
        `;
        
        const retryBtn = this.eventsContainer.querySelector('.retry-btn');
        if (retryBtn) {
            retryBtn.addEventListener('click', () => this.fetchEvents());
        }
    }

    updatePagination() {
        if (!this.paginationContainer) return;
        
        const paginationPagesEl = this.paginationContainer.querySelector('.pagination-pages');
        if (!paginationPagesEl) return;
        
        const hasPrev = this.currentPage > 1;
        const hasNext = this.currentPage < this.totalPages;
        
        paginationPagesEl.innerHTML = `
            ${hasPrev ? `
                <a class="pagination-link newer-posts" href="#">
                    &larr; Newer Events
                </a>
            ` : ''}
            <span class="pagination-current">Page ${this.currentPage} of ${this.totalPages}</span>
            ${hasNext ? `
                <a class="pagination-link older-posts" href="#">
                    Older Events &rarr;
                </a>
            ` : ''}
        `;
        
        // Show/hide pagination container based on total pages
        if (this.totalPages <= 1) {
            this.paginationContainer.style.display = 'none';
        } else {
            this.paginationContainer.style.display = 'block';
        }
    }

    showLoading() {
        // Create loading overlay if it doesn't exist
        if (!document.querySelector('.events-loading')) {
            const loadingEl = document.createElement('div');
            loadingEl.className = 'events-loading';
            loadingEl.innerHTML = `
                <div class="events-loading-spinner"></div>
                <p>Loading events...</p>
            `;
            document.querySelector('.gh-main').appendChild(loadingEl);
        }
        
        document.querySelector('.events-loading').style.display = 'flex';
    }

    hideLoading() {
        const loadingEl = document.querySelector('.events-loading');
        if (loadingEl) {
            loadingEl.style.display = 'none';
        }
    }
}

// Initialize events manager when the DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.events-feed')) {
        new RenobackEventsController();
    }
});
